import { AlertsInfoSections } from "./AlertsInfoSections";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HeaderTemplate } from "Components/templates/Header";
import { FooterTemplate } from "Components/templates/FooterTemplate";
import { ToastFeedback } from "Components/atoms/ToastFeedback";
import { Button } from "Components/atoms/Button";
import AlertCard from "Components/organisms/AlertCard";
import { AlertDialog } from "Components/molecules/AlertDialog";
import { AlertsHero } from "Components/organisms/AlertsHero";
import { sendGTMEvent } from "shared/services/GTM";
import { getPlatformType } from "shared/utils/platform";
import { GlobalStyles } from "styles/globalStyles";
import { theme } from "styles/theme";
import { ThemeProvider } from "styled-components";
import { AccountLoader } from "pages/Account/AccountLoader";
import {
  Page,
  HeaderArea,
  Content,
  Title,
  StateMessage,
  StateTitle,
  StateHint,
  SectionLabel,
  ListGrid,
  LoadMoreRow,
  DialogActions,
} from "pages/Alerts/styles";
import { ListHeader, NewAlertButton } from "./styles";
import { useAlertsList } from "./useAlertsList";
import type { Alert } from "shared/services/alerts/alerts.types";

/** Rota da tela de cadastro de alerta. */
export const ALERTS_CREATE_PATH = "/alertas/novo";

/**
 * Lista de alertas sem o formulário de cadastro (rota `/alertas`).
 *
 * A criação acontece em outra tela (`/alertas/novo`); ao salvar, o usuário
 * volta para cá com o feedback de sucesso em `location.state`.
 */
export default function AlertsListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    alerts,
    page,
    hasMore,
    loading,
    loadingMore,
    loadError,
    busyAlertIds,
    feedback,
    setFeedback,
    loadPage,
    toggle,
    remove,
  } = useAlertsList();

  const [alertToDelete, setAlertToDelete] = useState<Alert | null>(null);
  const feedbackConsumedRef = useRef(false);

  useEffect(() => {
    if (location.state?.feedback && !feedbackConsumedRef.current) {
      feedbackConsumedRef.current = true;
      setFeedback(location.state.feedback);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate, setFeedback]);

  const goToCreate = (source: "alerts_list" | "alerts_empty_state") => {
    sendGTMEvent("alertCreateClick", {
      platform: getPlatformType(),
      source,
    });
    navigate(ALERTS_CREATE_PATH);
  };

  const handleDeleteConfirm = () => {
    if (!alertToDelete) return;
    const target = alertToDelete;
    setAlertToDelete(null);
    remove(target);
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        <Page>
          <HeaderArea>
            <HeaderTemplate />
          </HeaderArea>
          <Content>
            <AccountLoader label="Carregando seus alertas..." fullPage />
          </Content>
          <FooterTemplate />
        </Page>
      </ThemeProvider>
    );
  }

  if (loadError) {
    return (
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        <Page>
          <HeaderArea>
            <HeaderTemplate />
          </HeaderArea>
          <Content>
            <Title>Meus Alertas</Title>
            <StateMessage>
              <StateTitle>Não foi possível carregar seus alertas.</StateTitle>
              <Button
                label="Tentar novamente"
                backgroundColor="mediumBlue"
                onClick={() => loadPage(1)}
                primary
              />
            </StateMessage>
          </Content>
          <FooterTemplate />
        </Page>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <Page>
        <HeaderArea>
          <HeaderTemplate />
        </HeaderArea>
        <AlertsHero />
        <Content>
          <ToastFeedback
            message={feedback?.message ?? ""}
            type={feedback?.type ?? "success"}
            isOpen={Boolean(feedback)}
            onClose={() => setFeedback(null)}
          />

          {alerts.length === 0 ? (
            <StateMessage>
              <StateTitle>Nenhum alerta criado</StateTitle>
              <StateHint>
                Crie um alerta e avisaremos quando o preço baixar.
              </StateHint>
              <NewAlertButton
                type="button"
                onClick={() => goToCreate("alerts_empty_state")}
              >
                ＋ Novo alerta
              </NewAlertButton>
            </StateMessage>
          ) : (
            <>
              <ListHeader>
                <SectionLabel>Lista de alertas criados</SectionLabel>
                <NewAlertButton
                  type="button"
                  onClick={() => goToCreate("alerts_list")}
                >
                  ＋ Novo alerta
                </NewAlertButton>
              </ListHeader>
              <ListGrid>
                {alerts.map((alert) => (
                  <AlertCard
                    key={alert.id}
                    alert={alert}
                    busy={busyAlertIds.has(alert.id)}
                    onToggle={toggle}
                    onDelete={setAlertToDelete}
                  />
                ))}
              </ListGrid>

              {hasMore && (
                <LoadMoreRow>
                  <Button
                    label={loadingMore ? "Carregando..." : "Carregar mais"}
                    backgroundColor="mediumBlue"
                    onClick={() => loadPage(page + 1)}
                    disabled={loadingMore}
                    primary
                  />
                </LoadMoreRow>
              )}
            </>
          )}
        </Content>
        <AlertsInfoSections />
        <FooterTemplate />
      </Page>

      <AlertDialog
        isOpen={Boolean(alertToDelete)}
        title="Tem certeza que deseja excluir esse alerta?"
        onClose={() => setAlertToDelete(null)}
        footer={
          <DialogActions>
            <Button
              label="Cancelar"
              backgroundColor="darkGray"
              onClick={() => setAlertToDelete(null)}
            />
            <Button
              label="Excluir"
              backgroundColor="red"
              onClick={handleDeleteConfirm}
              primary
            />
          </DialogActions>
        }
      >
        <p>Esta ação não pode ser desfeita.</p>
      </AlertDialog>
    </ThemeProvider>
  );
}
